export const LocalStorageVariables = {
  USER: "user",
  COSTUMER: "costumer",
  ENVIRONMENT: "env",
  REDIRECT_URL: "redirect_url",

  BIOMARKERS_LIST: "biomarkers_list",
  LOGIC_CATEGORIES_LIST: "logic_categories_list",
  NOTES: "notes",

  // Remembered column filters for the listings that opt in — see
  // `platform/components/common/recordTableFilterStore`. Cleared on logout.
  HISTORY_FILTERS: "history_filters",
  // The History Timeline's remembered selection, view, period and lane order,
  // per customer — see `platform/components/history/timeline/timelinePreferences`.
  // Cleared on logout.
  TIMELINE_PREFERENCES: "timeline_preferences",
};

export const enPrecisionMetric: Record<number, string> = {
  0: "None",
  1: "Lung",
  2: "Respiratory Function",
  3: "Metabolism",
  4: "Mitochondria",
  5: "Nervous System",
  6: "Posture",
  7: "Heart",
  8: "Endurance",
  9: "Recovery",
  10: "Brain",
  11: "Sexual Wellness (Male)",
  12: "Sexual Wellness (Female)",
  13: "Hunger Hormones",
  14: "Thyroid Hormones",
  15: "Stress Hormones",
  16: "Inflammation",
  17: "Sleep",
  18: "Gut",
  19: "Liver",
  20: "Pancreas",
  21: "Kidneys",
  22: "Bone",
  23: "Autoimmunity",
  24: "Nutritional Status",
  25: "Environmental Toxins",
  26: "Body Composition Analysis",
};

// Range fields (range_*) and qualitative fields (qualitative/qvalue/qoptions)
// come from the Precision biomarker endpoints. Range values are created only
// through Junction — the UI renders them but never authors them. Qualitative
// biomarkers carry a string `qvalue` (chosen from `qoptions`) instead of a
// numeric `value`.
export type BiomarkerRangeFields = {
  range_min_op?: string;
  range_max_op?: string;
  range_min_value?: number;
  range_max_value?: number;
};

// One recorded reading. The payload also carries an `active` flag, deliberately
// left off this type: it does not mark the value to display (the biomarker's
// own value/qvalue/range_* do that), and the UI lists every reading.
export type BiomarkerValueEntry = {
  biomarkerID: string;
  dateCreated: string;
  id: string;
  value: number;
  qvalue?: string | null;
  // Category this reading scored at its own date (enBiomarkerCategory:
  // 1 Borderline, 2 Normal, 3 Optimal, 4 Out of range).
  scoreCategory?: number;
} & BiomarkerRangeFields;

// One reference-range segment, gender-resolved by the backend. Ordered low →
// high; a null bound is open-ended. `label` is ignored by the UI — segments
// are named from `category` via the enum.
export type BiomarkerRange = {
  category: number;
  label?: string;
  min: number | null;
  max: number | null;
};

// Change between the current reading and the previous one, computed by the
// backend (pct is signed and unrounded).
export type BiomarkerChange = {
  pct: number;
  direction: string;
  previousValue: number;
  previousDate: string;
};

export type BiomarkerItem = {
  biomarkerID: string;
  userID: string;
  testKind: number;
  metrics: number[];
  name: string;
  label: string | null;
  shortName: string | null;
  unit: string;
  value: number;
  max: number;
  min: number;
  values: BiomarkerValueEntry[];
  scoreCategory: number;
  ranges?: BiomarkerRange[] | null;
  change?: BiomarkerChange | null;
  qualitative?: boolean;
  qvalue?: string | null;
  qoptions?: string[];
} & BiomarkerRangeFields;

export type BiomarkerGroupedData = {
  [metric: number]: BiomarkerItem[];
};

// Raw item returned by the biomarker extractor endpoint. `convertedValue` is
// absent when no conversion is needed (unit_match === true), `null` when a
// conversion is needed but not possible (rejected), and the converted value
// when done — a string (numeric like "6980.00" or a range like "<0.05"),
// though older payloads sent a number.
export type ExtractorResult = {
  biomarkerID: string | null;
  name: string; // detected name from the uploaded document
  list_name: string | null; // canonical DB name, null when unmatched
  value: string; // detected value (string)
  unit: string; // detected unit
  list_unit: string; // target/DB unit
  matched: boolean;
  unit_match: boolean;
  convertedValue?: number | string | null;
  // Keragon only: the EHR's own id for this reading, saved as `batchId`.
  keragonId?: number | string | null;
};

export type ExtractorResponse = {
  measurementDate: string;
  results: ExtractorResult[];
};

export type ExtractorJobStatus = "Running" | "Failed" | "Done";

// Returned by the extractor status poll. `result` is present only when
// status === "Done"; `error`/`message` may be present when status === "Failed".
export type ExtractorJobResponse = {
  jobId: string;
  status: ExtractorJobStatus;
  createdAt?: string;
  result?: ExtractorResponse;
  error?: string;
  message?: string;
};

// Per-item payload for the batch UpdateBiomarkers endpoint. userId + date
// travel with each item; the value is numeric (newValue), qualitative (qvalue),
// or a range (range_* bounds).
export type UpdateBiomarkerBatchItem = {
  biomarkerId: string;
  userId: string;
  date: string;
  newValue?: number;
  qvalue?: string;
  // Keragon imports only: the EHR's id for the reading (result `keragonId`).
  batchId?: string;
} & BiomarkerRangeFields;
